import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Alert, Slider, Button, Spin, Empty, Typography, Tabs } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import PageContainer from '@/components/PageContainer';
import WeatherMapModal from '@/components/WeatherMapModal';
import WeatherBulletinValidationCard from '@/components/WeatherBulletinValidationCard';
import WeatherPowerIndexPanel from '@/components/WeatherPowerIndexPanel';
import HighPvRealizationBacktestPanel from '@/components/HighPvRealizationBacktestPanel';
import WeatherTriSourcePanel from '@/components/WeatherTriSourcePanel';
import WeatherTradingAdviceHero from '@/components/WeatherTradingAdviceHero';
import WeatherBriefTab from '@/components/WeatherBriefTab';
import WeatherEvidenceTab from '@/components/WeatherEvidenceTab';
import WeatherHistoryPlaybackTab from '@/components/WeatherHistoryPlaybackTab';
import { useWeatherTradingAdvice } from '@/api/weatherTradingAdvice';
import {
  useMapForecast,
  type VariableKey,
  type PrefectureName,
  VARIABLE_META,
  PREFECTURES,
  getDayMax,
  getPrefecture,
} from '@/api/weatherMap';

const { Text } = Typography;

const VARIABLE_TABS: { key: VariableKey; label: string }[] = [
  { key: 'ghi', label: '辐照' },
  { key: 'cloud', label: '云量' },
  { key: 'wind', label: '风速' },
  { key: 'precip', label: '降水' },
];

const VAR_COLORS: Record<VariableKey, [string, string]> = {
  ghi: ['#f5f5f0', '#e87c20'],
  cloud: ['#fafafa', '#4a4a4a'],
  wind: ['#e8f4fd', '#1a5fa8'],
  precip: ['#e8f8e8', '#1a7a1a'],
};

interface CountyHover {
  name: string;
  shortName: string;
  prefecture: PrefectureName;
  ghi: number | null;
  cloud: number | null;
  wind: number | null;
  precip: number | null;
  x: number;
  y: number;
}

const WeatherForecastMapPage: React.FC = () => {
  const { data: mapData, isLoading: mapLoading, error: mapError, refetch } = useMapForecast();
  const { data: adviceData, isLoading: adviceLoading, error: adviceError } = useWeatherTradingAdvice();
  const [activeTab, setActiveTab] = useState('advice');
  const [hour, setHour] = useState(12);
  const [variable, setVariable] = useState<VariableKey>('ghi');
  const [playing, setPlaying] = useState(false);
  const [selectedPrefecture, setSelectedPrefecture] = useState<PrefectureName | null>(null);
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [hoveredCounty, setHoveredCounty] = useState<CountyHover | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const playTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/geo/shandong_counties.json').then((r) => r.json()),
      fetch('/geo/shandong_province.json').then((r) => r.json()),
    ])
      .then(([countiesGeo, prefGeo]) => {
        if (cancelled) return;
        echarts.registerMap('shandong_counties', countiesGeo);
        echarts.registerMap('shandong_prefectures', prefGeo);
        setGeoLoaded(true);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
      echarts.registerMap('shandong_counties', {} as never);
      echarts.registerMap('shandong_prefectures', {} as never);
    };
  }, []);

  useEffect(() => {
    if (playing && activeTab === 'evidence') {
      playTimerRef.current = setInterval(() => {
        setHour((h) => (h >= 23 ? 0 : h + 1));
      }, 800);
    } else if (playTimerRef.current) {
      clearInterval(playTimerRef.current);
    }
    return () => {
      if (playTimerRef.current) clearInterval(playTimerRef.current);
    };
  }, [playing, activeTab]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (activeTab !== 'evidence') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') setHour((h) => Math.max(0, h - 1));
      if (e.key === 'ArrowRight') setHour((h) => Math.min(23, h + 1));
      if (e.key === ' ') { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab]);

  const provinceMax = useMemo(() => {
    if (!mapData?.cities) return 100;
    const maxValue = getDayMax(mapData.cities, variable);
    return maxValue || 100;
  }, [mapData, variable]);

  const countySeriesData = useMemo(() => {
    if (!mapData?.cities) return [];
    return mapData.cities.map((city) => {
      const pref = getPrefecture(city.name);
      const shortName = pref ? city.name.replace(pref, '') : city.name;
      const hd = city.hours[hour];
      return {
        name: shortName,
        value: hd?.[variable] ?? undefined,
        ghi: hd?.ghi ?? null,
        cloud: hd?.cloud ?? null,
        wind: hd?.wind ?? null,
        precip: hd?.precip ?? null,
        dbName: city.name,
        prefecture: pref,
      };
    });
  }, [mapData, hour, variable]);

  const countyLookup = useMemo(() => {
    const lookup = new Map<string, typeof countySeriesData[number]>();
    for (const row of countySeriesData) {
      lookup.set(row.name, row);
    }
    return lookup;
  }, [countySeriesData]);

  useEffect(() => {
    if (activeTab !== 'evidence' || !chartRef.current || !geoLoaded || !mapData?.cities?.length) return;

    if (chartInstance.current) chartInstance.current.dispose();
    const chart = echarts.init(chartRef.current);
    chartInstance.current = chart;
    const [colorLow, colorHigh] = VAR_COLORS[variable];

    chart.setOption({
      tooltip: { show: false },
      visualMap: {
        min: 0,
        max: provinceMax,
        inRange: { color: [colorLow, colorHigh] },
        calculable: false,
        show: false,
        seriesIndex: 0,
      },
      series: [
        {
          type: 'map',
          map: 'shandong_counties',
          roam: false,
          label: { show: false },
          itemStyle: { borderColor: 'transparent', borderWidth: 0 },
          emphasis: {
            label: { show: false },
            itemStyle: { areaColor: '#ffd591', borderColor: 'transparent', borderWidth: 0 },
          },
          data: countySeriesData,
        },
        {
          type: 'map',
          map: 'shandong_prefectures',
          roam: false,
          silent: true,
          label: { show: true, color: '#555', fontSize: 13, fontWeight: 'bold' },
          emphasis: { label: { show: true } },
          itemStyle: { areaColor: 'transparent', borderColor: '#555', borderWidth: 2 },
          data: PREFECTURES.map((prefecture) => ({ name: prefecture, value: 0 })),
        },
      ],
    });

    chart.off('mousemove');
    chart.on('mousemove', (params: unknown) => {
      const p = params as { name?: string; event?: { offsetX?: number; offsetY?: number } };
      if (p.name) {
        const row = countyLookup.get(p.name);
        if (row?.prefecture) {
          setHoveredCounty({
            name: row.dbName,
            shortName: row.name,
            prefecture: row.prefecture,
            ghi: row.ghi,
            cloud: row.cloud,
            wind: row.wind,
            precip: row.precip,
            x: p.event?.offsetX ?? 0,
            y: p.event?.offsetY ?? 0,
          });
          return;
        }
      }
      setHoveredCounty(null);
    });
    chart.off('mouseout');
    chart.on('mouseout', () => setHoveredCounty(null));
    chart.off('click');
    chart.on('click', (params: unknown) => {
      const p = params as { name?: string };
      if (!p.name) return;
      const row = countyLookup.get(p.name);
      if (row?.prefecture) setSelectedPrefecture(row.prefecture);
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
      chartInstance.current = null;
    };
  }, [activeTab, geoLoaded, countySeriesData, variable, provinceMax, mapData, countyLookup]);

  const renderMapEvidence = () => {
    if (mapLoading || !geoLoaded) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 360 }}>
          <Spin tip="加载地图气象数据..." />
        </div>
      );
    }
    if (mapError || !mapData) {
      return (
        <Empty description="地图气象数据加载失败">
          <Button onClick={() => refetch()}>重新加载</Button>
        </Empty>
      );
    }
    if (mapData.data_freshness === 'empty') {
      return <Empty description={mapData.data_freshness_note ?? '暂无明日预报数据'} />;
    }

    return (
      <>
        {mapData.data_freshness === 'stale' && (
          <Alert message={mapData.data_freshness_note} type="warning" showIcon closable style={{ marginBottom: 12 }} />
        )}

        <Tabs
          activeKey={variable}
          onChange={(key) => setVariable(key as VariableKey)}
          style={{ marginBottom: 0 }}
          items={VARIABLE_TABS.map((tab) => ({ key: tab.key, label: tab.label }))}
          tabBarExtraContent={(
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 8 }}>
              <div
                style={{
                  width: 120,
                  height: 12,
                  background: `linear-gradient(to right, ${VAR_COLORS[variable][0]}, ${VAR_COLORS[variable][1]})`,
                  borderRadius: 4,
                }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                0 - {Math.round(provinceMax)} {VARIABLE_META[variable].unit}
              </Text>
            </div>
          )}
        />

        <div style={{ position: 'relative' }}>
          <div
            ref={chartRef}
            style={{ width: '100%', height: 'calc(100vh - 380px)', minHeight: 400, cursor: 'pointer' }}
          />

          {hoveredCounty && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(hoveredCounty.x + 16, (chartRef.current?.clientWidth ?? 800) - 200),
                top: Math.min(hoveredCounty.y + 12, (chartRef.current?.clientHeight ?? 500) - 200),
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 8,
                padding: '12px 16px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                fontSize: 13,
                lineHeight: 1.8,
                zIndex: 10,
                pointerEvents: 'none',
                minWidth: 160,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>{hoveredCounty.name}</div>
              {VARIABLE_TABS.map((tab) => {
                const value = hoveredCounty[tab.key as 'ghi' | 'cloud' | 'wind' | 'precip'] as number | null;
                const meta = VARIABLE_META[tab.key];
                const highlight = tab.key === variable;
                return (
                  <div key={tab.key} style={{ color: highlight ? '#1677ff' : '#666', fontWeight: highlight ? 600 : 400 }}>
                    {tab.label} {value !== null ? `${Math.round(value * 10) / 10} ${meta.unit}` : '无数据'}
                  </div>
                );
              })}
              <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #f0f0f0', fontSize: 11, color: '#999' }}>
                点击查看{hoveredCounty.prefecture}详情
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fafafa', borderRadius: 8, marginTop: 8 }}>
          <Button size="small" icon={<LeftOutlined />} disabled={hour === 0} onClick={() => setHour((h) => Math.max(0, h - 1))} />
          <Button size="small" icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={() => setPlaying((p) => !p)} />
          <Slider
            style={{ flex: 1 }}
            min={0}
            max={23}
            value={hour}
            onChange={setHour}
            marks={{ 0: '00时', 6: '06时', 12: '12时', 18: '18时', 23: '23时' }}
            tooltip={{ formatter: (value) => `${value}时` }}
          />
          <Button size="small" icon={<RightOutlined />} disabled={hour === 23} onClick={() => setHour((h) => Math.min(23, h + 1))} />
          <Text strong style={{ minWidth: 48, textAlign: 'center' }}>{hour}时</Text>
        </div>

        <WeatherMapModal
          open={selectedPrefecture !== null}
          prefecture={selectedPrefecture}
          allCities={mapData.cities}
          hour={hour}
          variable={variable}
          provinceMax={provinceMax}
          onClose={() => setSelectedPrefecture(null)}
          onHourChange={setHour}
          onPlayingChange={setPlaying}
          playing={playing}
        />
      </>
    );
  };

  return (
    <PageContainer title="新能源出力研判">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <Text strong>
          D+1 预测日期 {adviceData?.date ?? mapData?.date ?? ''} · {(adviceData?.model_name ?? mapData?.model ?? 'ecmwf_ifs').replace('_', ' ')}
        </Text>
        <div style={{ flex: 1 }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          建议生成于 {adviceData?.generated_at ? new Date(adviceData.generated_at).toLocaleString('zh-CN', { hour12: false }) : '加载中'}
        </Text>
      </div>

      {adviceLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
          <Spin tip="生成交易建议..." />
        </div>
      )}

      {adviceError && (
        <Alert type="warning" showIcon message="交易建议暂不可用" description="详细气象证据仍可在接口恢复后查看。" style={{ marginBottom: 12 }} />
      )}

      {adviceData?.data_freshness === 'stale' && adviceData.message && (
        <Alert
          type="warning"
          showIcon
          message="气象建议数据需复核"
          description={adviceData.message}
          style={{ marginBottom: 12 }}
        />
      )}

      {adviceData && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'advice',
              label: '交易建议',
              children: (
                <>
                  <WeatherTradingAdviceHero data={adviceData} onOpenEvidence={() => setActiveTab('evidence')} />
                  <WeatherPowerIndexPanel />
                </>
              ),
            },
            {
              key: 'brief',
              label: '气象简报',
              children: <WeatherBriefTab data={adviceData} />,
            },
            {
              key: 'evidence',
              label: '详细佐证',
              children: (
                <WeatherEvidenceTab data={adviceData}>
                  <WeatherTriSourcePanel />
                  <HighPvRealizationBacktestPanel />
                  <WeatherBulletinValidationCard />
                  {renderMapEvidence()}
                </WeatherEvidenceTab>
              ),
            },
            {
              key: 'history',
              label: '历史效果',
              children: <WeatherHistoryPlaybackTab data={adviceData} />,
            },
          ]}
        />
      )}
    </PageContainer>
  );
};

export default WeatherForecastMapPage;
